import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    truck: 'M1 3h15v13H1V3zm15 8h4l3 3v2h-7v-5zM5 18a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm12 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
    search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
    building: 'M3 21h18M3 7v14m18-14v14M3 7l9-4 9 4M5 21V11m4 10V11m6 10V11m4 10V11',
    calendar: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
    fuel: 'M13 2a2 2 0 0 0-2 2v1H7a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-4V4a2 2 0 0 0-2-2z',
    money: 'M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6',
    x: 'M18 6L6 18 M6 6l12 12'
};

const defaultVehicle = {
    vendorId: '',
    vehicleCode: '',
    vehicleType: '',
    registrationNumber: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    fuelType: '',
    color: '#ffffff',
    purchasePrice: '',
    acquisitionDate: '',
    depreciationMonthly: '',
    gpsMonthly: '',
    dashcamMonthly: '',
    insuranceMonthly: '',
    otherCostsMonthly: '',
    efficiency: '',
    status: 'Under Review',
    seatingCapacity: '',
    insuranceExpiry: '',
    registrationExpiry: '',
    vehicleLicenseNumber: '',
    vehicleLicenseExpiry: '',
    fuelStickerNumber: '',
    insuranceProvider: '',
    policyNumber: '',
    fleetReceivedDate: '',
    photo: '',
    documents: []
};

export default function CreateVendorFleet({ user, editVehicle, onBack, onSaved, operatorDetails, masterData = {} }) {
    const [activeTab, setActiveTab] = useState('Identification');
    const [formData, setFormData] = useState(defaultVehicle);
    const [documentsList, setDocumentsList] = useState([{ name: '', file: null }]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [documentTypes, setDocumentTypes] = useState([]);

    const tabs = ['Identification', 'Registration & Insurance', 'Financials', 'Documents'];

    useEffect(() => {
        if (editVehicle) {
            setFormData({
                ...defaultVehicle,
                ...editVehicle,
                vendorId: typeof editVehicle.vendorId === 'object' ? editVehicle.vendorId?._id : (editVehicle.vendorId || ''),
                acquisitionDate: editVehicle.acquisitionDate ? editVehicle.acquisitionDate.split('T')[0] : '',
                insuranceExpiry: editVehicle.insuranceExpiry ? editVehicle.insuranceExpiry.split('T')[0] : '',
                registrationExpiry: editVehicle.registrationExpiry ? editVehicle.registrationExpiry.split('T')[0] : '',
                vehicleLicenseExpiry: editVehicle.vehicleLicenseExpiry ? editVehicle.vehicleLicenseExpiry.split('T')[0] : '',
                fleetReceivedDate: editVehicle.fleetReceivedDate ? editVehicle.fleetReceivedDate.split('T')[0] : '',
            });
        } else if (user?.userType === 'vendor') {
            setFormData(prev => ({ ...prev, vendorId: user._id }));
        }

        // Fetch document types if not provided in masterData
        if (masterData['document-types']) {
            setDocumentTypes(masterData['document-types']);
        } else {
            fetch('/api/master-data')
                .then(r => r.json())
                .then(data => {
                    setDocumentTypes(data.filter(d => d.category === 'document-types' && d.active !== false));
                })
                .catch(err => console.error('Error fetching master data:', err));
        }
    }, [editVehicle, user, masterData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (evt) => setFormData(prev => ({ ...prev, photo: evt.target.result }));
            reader.readAsDataURL(file);
        }
    };

    const handleAddDocument = () => {
        setDocumentsList(prev => [...prev, { name: '', file: null }]);
    };

    const handleDocumentChange = (index, field, value) => {
        const updated = [...documentsList];
        updated[index][field] = value;
        setDocumentsList(updated);
    };

    const handleRemoveDocument = (index) => {
        const updated = [...documentsList];
        updated.splice(index, 1);
        setDocumentsList(updated);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        const data = new FormData();
        const skipKeys = new Set(['_id', '__v', 'documents', 'createdAt', 'updatedAt']);
        
        Object.keys(formData).forEach(key => {
            if (skipKeys.has(key)) return;
            const val = formData[key];
            if (val !== undefined && val !== null) {
                data.append(key, val);
            }
        });

        // Use operatorId from context if available
        const operatorId = user?.operatorId || user?._id || operatorDetails?._id;
        if (operatorId && !data.has('operatorId')) {
            data.append('operatorId', operatorId);
        }
        
        data.append('existingDocuments', JSON.stringify(formData.documents || []));
        
        const documentNames = [];
        documentsList.forEach(doc => {
            if (doc.file) {
                data.append('documents', doc.file);
                documentNames.push(doc.name || 'Document');
            }
        });
        if (documentNames.length > 0) {
            data.append('documentNames', JSON.stringify(documentNames));
        }

        const url = editVehicle ? `/api/vendor-fleet/${editVehicle._id}` : `/api/vendor-fleet`;
        const method = editVehicle ? 'put' : 'post';

        try {
            const res = await axios[method](url, data);
            onSaved();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save vehicle');
        } finally {
            setLoading(false);
        }
    };

    const isVendor = user?.userType === 'vendor';

    return (
        <div className="create-fleet-wrapper">
            <style>{`
                .create-fleet-wrapper {
                    height: 100%;
                    width: 100%;
                    overflow: hidden;
                    background: #f8fafc;
                    font-family: 'Inter', sans-serif;
                    display: flex;
                }
                .ts-sidebar {
                    width: 280px;
                    background: white;
                    border-right: 1px solid #e2e8f0;
                    padding: 32px 16px;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    flex-shrink: 0;
                    height: 100%;
                    overflow-y: auto;
                }
                .ts-nav-item {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 12px 16px;
                    border-radius: 12px;
                    border: none;
                    background: transparent;
                    color: #64748b;
                    font-size: 0.9375rem;
                    font-weight: 600;
                    cursor: pointer;
                    text-align: left;
                    transition: all 0.2s;
                }
                .ts-nav-item:hover { background: #f8fafc; color: #16539C; }
                .ts-nav-item.active { background: #ecfdf5; color: #059669; }
                
                .ts-main {
                    flex: 1;
                    padding: 40px;
                    overflow-y: auto;
                    display: flex;
                    flex-direction: column;
                    gap: 32px;
                }
                .ts-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 8px;
                }
                .ts-title-group h1 { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0; }
                .ts-title-group p { font-size: 14px; color: #64748b; margin: 4px 0 0; }
                .back-btn-premium {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 16px;
                    border-radius: 10px;
                    border: 1.5px solid #10b98140;
                    background: white;
                    color: #64748b;
                    font-weight: 600;
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .back-btn-premium:hover { background: #f8fafc; color: #1e293b; }
                
                .form-panel {
                    background: white;
                    border-radius: 20px;
                    padding: 32px;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
                    border: 1px solid #e2e8f0;
                    max-width: 1000px;
                }
                .section-title { font-size: 1.125rem; font-weight: 700; margin-bottom: 24px; color: #1e293b; }
                .form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; }
                .form-group { display: flex; flex-direction: column; gap: 8px; }
                .span-2 { grid-column: span 2; }
                .form-label { font-size: 12px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; display: flex; align-items: center; gap: 6px; }
                
                .input-wrapper { position: relative; display: flex; align-items: center; width: 100%; }
                .input-icon { position: absolute; left: 14px; color: #94a3b8; pointer-events: none; }
                
                .form-input, .form-select {
                    width: 100%;
                    padding: 12px 16px 12px 42px;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 12px;
                    font-size: 14px;
                    transition: all 0.2s;
                    background: #fff;
                    color: #1e293b;
                    outline: none;
                    box-sizing: border-box;
                }
                .form-input:focus, .form-select:focus { border-color: #10b981; box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.1); }
                .form-input:focus + .input-icon { color: #10b981; }
                
                .footer-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 16px;
                    margin-top: 40px;
                    padding-top: 32px;
                    border-top: 1px solid #e2e8f0;
                }
                .btn-premium {
                    padding: 12px 28px;
                    border-radius: 12px;
                    font-weight: 700;
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.2s;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .btn-cancel { background: white; border: 1.5px solid #e2e8f0; color: #64748b; }
                .btn-cancel:hover { background: #f1f5f9; color: #1e293b; }
                .btn-save { background: #10b981; border: none; color: white; box-shadow: 0 4px 6px -1px rgba(16, 185, 129, 0.3); }
                .btn-save:hover { background: #059669; transform: translateY(-1px); }
            `}</style>

            <aside className="ts-sidebar">
                <div className="ts-nav-item active" style={{ marginBottom: 24, cursor: 'default', background: 'none', paddingLeft: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#10b981' }}>Fleet Vehicle</span>
                </div>
                {tabs.map(tab => (
                    <button
                        key={tab}
                        className={`ts-nav-item ${activeTab === tab ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab)}
                        type="button"
                    >
                        {tab === 'Identification' && <Icon d={icons.truck} />}
                        {tab === 'Registration & Insurance' && <Icon d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8" />}
                        {tab === 'Financials' && <Icon d={icons.money} />}
                        {tab === 'Documents' && <Icon d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z M22 6l-10 7L2 6" />}
                        <span>{tab}</span>
                    </button>
                ))}
            </aside>

            <main className="ts-main">
                <header className="ts-header">
                    <div className="ts-title-group">
                        <h1>{editVehicle ? 'Edit Vehicle' : 'Register New Vehicle'}</h1>
                        <p>{editVehicle ? 'Update details for this fleet vehicle' : 'Add a new vehicle to your fleet'}</p>
                    </div>
                    <button className="back-btn-premium" onClick={onBack}>
                        <Icon d="M19 12H5M12 19l-7-7 7-7" size={16} strokeWidth={2.5} />
                        Back to List
                    </button>
                </header>

                {error && (
                    <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '16px 24px', borderRadius: 16, border: '1px solid #fee2e2', display: 'flex', alignItems: 'center', gap: 12, fontSize: 14, fontWeight: 500 }}>
                        <Icon d="M12 8v4M12 16h.01M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10z" strokeWidth={2} />
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} style={{ display: 'contents' }}>
                    <div className="form-panel">
                        {activeTab === 'Identification' && (
                            <>
                                <div className="section-title">Vehicle Identification</div>
                                <div className="form-grid">
                                    <div className="form-group span-2">
                                        <label className="form-label">Vehicle Type *</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.truck} size={16} className="input-icon" />
                                            <select className="form-select" name="vehicleType" required value={formData.vehicleType || ''} onChange={handleChange}>
                                                <option value="">Select type</option>
                                                {(masterData['vehicle-types'] || []).map(vt => (
                                                    <option key={vt._id} value={vt.name}>{vt.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Vehicle ID / Code</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.truck} size={16} className="input-icon" />
                                            <input className="form-input" name="vehicleCode" placeholder="e.g. V-001" value={formData.vehicleCode || ''} onChange={handleChange} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Registration Number *</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.search} size={16} className="input-icon" />
                                            <input className="form-input" name="registrationNumber" required placeholder="e.g. DXB-12345" value={formData.registrationNumber || ''} onChange={handleChange} />
                                        </div>
                                    </div>
                                </div>

                                <div style={{ marginBottom: 24, marginTop: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
                                    <div style={{ width: 80, height: 80, borderRadius: 16, background: '#f1f5f9', border: '2px dashed #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
                                        {formData.photo ? (
                                            <img src={formData.photo} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <Icon d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" size={28} color="#94a3b8" />
                                        )}
                                        <input 
                                            type="file" accept="image/*"
                                            style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                                            onChange={handlePhotoChange}
                                        />
                                    </div>
                                    <div>
                                        <h4 style={{ margin: '0 0 4px', fontSize: 13, color: '#1e293b', fontWeight: 600 }}>Upload Photo</h4>
                                        <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>Click or drag image to upload.</p>
                                    </div>
                                </div>

                                <div className="section-title" style={{ marginTop: 32 }}>Vehicle Details</div>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="form-label">Make *</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.truck} size={16} className="input-icon" />
                                            <select className="form-select" name="make" required value={formData.make || ''} onChange={handleChange}>
                                                <option value="">Select Make...</option>
                                                {(masterData['vehicle-makes'] || []).map(m => <option key={m._id} value={m.name}>{m.name}</option>)}
                                                {formData.make && !(masterData['vehicle-makes'] || []).find(m => m.name === formData.make) && <option value={formData.make}>{formData.make}</option>}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Model</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.truck} size={16} className="input-icon" />
                                            <select className="form-select" name="model" value={formData.model || ''} onChange={handleChange}>
                                                <option value="">Select Model...</option>
                                                {(masterData['vehicle-models'] || []).map(m => <option key={m._id} value={m.name}>{m.name}</option>)}
                                                {formData.model && !(masterData['vehicle-models'] || []).find(m => m.name === formData.model) && <option value={formData.model}>{formData.model}</option>}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Year</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.calendar} size={16} className="input-icon" />
                                            <input className="form-input" name="year" type="number" min="1990" max={new Date().getFullYear() + 1} placeholder={new Date().getFullYear()} value={formData.year || ''} onChange={handleChange} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Fuel Type</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.fuel} size={16} className="input-icon" />
                                            <select className="form-select" name="fuelType" value={formData.fuelType || ''} onChange={handleChange}>
                                                <option value="">Select fuel type</option>
                                                <option>Petrol</option>
                                                <option>Diesel</option>
                                                <option>Electric</option>
                                                <option>Hybrid</option>
                                                <option>CNG</option>
                                                <option>LPG</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Color</label>
                                        <div className="input-wrapper" style={{ gap: 10 }}>
                                            <input type="color" name="color" value={formData.color || '#ffffff'} onChange={handleChange} style={{ width: 42, height: 42, border: '2px solid #e2e8f0', borderRadius: 10, cursor: 'pointer', padding: 2, background: 'none' }} />
                                            <input className="form-input" name="color" style={{ paddingLeft: 16 }} placeholder="e.g. White" value={formData.color || ''} onChange={handleChange} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Fleet Received Date</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.calendar} size={16} className="input-icon" />
                                            <input className="form-input" name="fleetReceivedDate" type="date" value={formData.fleetReceivedDate} onChange={handleChange} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Seating Capacity</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.truck} size={16} className="input-icon" />
                                            <input className="form-input" name="seatingCapacity" type="number" placeholder="e.g. 15" value={formData.seatingCapacity || ''} onChange={handleChange} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Fuel Sticker Number</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.search} size={16} className="input-icon" />
                                            <input className="form-input" name="fuelStickerNumber" placeholder="e.g. FS-123" value={formData.fuelStickerNumber || ''} onChange={handleChange} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Efficiency</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.fuel} size={16} className="input-icon" />
                                            <input className="form-input" name="efficiency" placeholder="e.g. 15 km/l" value={formData.efficiency || ''} onChange={handleChange} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Status</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.truck} size={16} className="input-icon" />
                                            <select className="form-select" name="status" value={formData.status || 'Under Review'} onChange={handleChange}>
                                                <option value="Under Review">Under Review</option>
                                                <option value="Active">Active</option>
                                                <option value="Inactive">Inactive</option>
                                                <option value="Under Maintenance">Under Maintenance</option>
                                                <option value="Damaged">Damaged</option>
                                                <option value="Retired">Retired</option>
                                                <option value="In Transit">In Transit</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {activeTab === 'Registration & Insurance' && (
                            <>
                                <div className="section-title">Registration & License</div>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="form-label">Registration Expiry Date</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.calendar} size={16} className="input-icon" />
                                            <input className="form-input" name="registrationExpiry" type="date" value={formData.registrationExpiry || ''} onChange={handleChange} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Vehicle License / Permit No.</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.search} size={16} className="input-icon" />
                                            <input className="form-input" name="vehicleLicenseNumber" placeholder="e.g. LIC-9988" value={formData.vehicleLicenseNumber || ''} onChange={handleChange} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">License Expiry Date</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.calendar} size={16} className="input-icon" />
                                            <input className="form-input" name="vehicleLicenseExpiry" type="date" value={formData.vehicleLicenseExpiry || ''} onChange={handleChange} />
                                        </div>
                                    </div>
                                </div>

                                <div className="section-title" style={{ marginTop: 32 }}>Insurance Details</div>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="form-label">Insurance Provider</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.search} size={16} className="input-icon" />
                                            <input className="form-input" name="insuranceProvider" placeholder="e.g. Al Ahliya" value={formData.insuranceProvider || ''} onChange={handleChange} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Policy Number</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.search} size={16} className="input-icon" />
                                            <input className="form-input" name="policyNumber" placeholder="e.g. POL-123456" value={formData.policyNumber || ''} onChange={handleChange} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Insurance Expiry Date</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.calendar} size={16} className="input-icon" />
                                            <input className="form-input" name="insuranceExpiry" type="date" value={formData.insuranceExpiry || ''} onChange={handleChange} />
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {activeTab === 'Financials' && (
                            <>
                                <div className="section-title">Financial Details</div>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="form-label">Purchase Price</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.money} size={16} className="input-icon" />
                                            <input className="form-input" name="purchasePrice" type="number" step="0.01" placeholder="0.00" value={formData.purchasePrice || ''} onChange={handleChange} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Acquisition Date</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.calendar} size={16} className="input-icon" />
                                            <input className="form-input" name="acquisitionDate" type="date" value={formData.acquisitionDate || ''} onChange={handleChange} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Depreciation / Month</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.money} size={16} className="input-icon" />
                                            <input className="form-input" name="depreciationMonthly" type="number" step="0.01" placeholder="0.00" value={formData.depreciationMonthly || ''} onChange={handleChange} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">GPS Fee / Month</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.money} size={16} className="input-icon" />
                                            <input className="form-input" name="gpsMonthly" type="number" step="0.01" placeholder="0.00" value={formData.gpsMonthly || ''} onChange={handleChange} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Dashcam Fee / Month</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.money} size={16} className="input-icon" />
                                            <input className="form-input" name="dashcamMonthly" type="number" step="0.01" placeholder="0.00" value={formData.dashcamMonthly || ''} onChange={handleChange} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Insurance / Month</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.money} size={16} className="input-icon" />
                                            <input className="form-input" name="insuranceMonthly" type="number" step="0.01" placeholder="0.00" value={formData.insuranceMonthly || ''} onChange={handleChange} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Other Costs / Month</label>
                                        <div className="input-wrapper">
                                            <Icon d={icons.money} size={16} className="input-icon" />
                                            <input className="form-input" name="otherCostsMonthly" type="number" step="0.01" placeholder="0.00" value={formData.otherCostsMonthly || ''} onChange={handleChange} />
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {activeTab === 'Documents' && (
                            <>
                                <div className="section-title">Vehicle Documents</div>
                                <div className="form-group span-2" style={{ background: '#f8fafc', padding: 20, borderRadius: 16, border: '1px solid #e2e8f0' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                        <label className="form-label" style={{ margin: 0 }}>Vehicle Documents</label>
                                        <button type="button" onClick={handleAddDocument} style={{ background: '#eef2ff', color: '#16539C', border: 'none', padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <Icon d="M12 5v14M5 12h14" /> Add Document
                                        </button>
                                    </div>
                                    
                                    {editVehicle?.documents?.length > 0 && (
                                        <div style={{ marginBottom: 20 }}>
                                            <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b', marginBottom: 8 }}>Existing Documents</div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                {editVehicle.documents.map((doc, idx) => (
                                                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'white', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                                                        <Icon d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6" size={18} />
                                                        <span style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{doc.name || 'Document'}</span>
                                                        <a href={`/api/uploads/${doc.file}`} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 'auto', fontSize: 12, color: '#16539C', textDecoration: 'none', fontWeight: 600 }}>View</a>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                        {documentsList.map((doc, index) => (
                                            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                <select
                                                    className="form-select"
                                                    value={doc.name}
                                                    onChange={e => handleDocumentChange(index, 'name', e.target.value)}
                                                    style={{ flex: 1, padding: '12px 16px', border: '1.5px solid #e2e8f0', borderRadius: '12px', fontSize: '14px', fontFamily: "'Inter', sans-serif", fontWeight: 500, color: '#1e293b' }}
                                                >
                                                    <option value="">Select Document Type</option>
                                                    {documentTypes.length === 0 ? (
                                                        <option disabled value="">⚙ No options — add in Settings → Master Data</option>
                                                    ) : (
                                                        documentTypes.map(dt => (
                                                            <option key={dt._id} value={dt.name}>{dt.name}</option>
                                                        ))
                                                    )}
                                                    {doc.name && !documentTypes.some(d => d.name === doc.name) && (
                                                        <option value={doc.name}>{doc.name}</option>
                                                    )}
                                                </select>
                                                <input 
                                                    className="form-input" 
                                                    type="file" 
                                                    onChange={e => handleDocumentChange(index, 'file', e.target.files[0])} 
                                                    style={{ flex: 1 }}
                                                />
                                                {documentsList.length > 1 && (
                                                    <button type="button" onClick={() => handleRemoveDocument(index)} style={{ background: '#fee2e2', color: '#ef4444', border: 'none', padding: 10, borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Icon d={icons.x} size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="footer-actions">
                            <button type="button" className="btn-premium btn-cancel" onClick={onBack}>Cancel</button>
                            <button type="submit" className="btn-premium btn-save" disabled={loading}>
                                {loading ? 'Saving...' : 'Save Fleet Vehicle'}
                            </button>
                        </div>
                    </div>
                </form>
            </main>
        </div>
    );
}
